import { useState } from "react";
import { t } from "i18next";

import {
  createPanel,
  createViewport,
  isDeadViewport,
  isDefaultInstanceConditions,
} from "../../helpers/hangingProtocol/hpDocument";
import { getOperands } from "../../helpers/hangingProtocol/conditionDsl";
import { resizeGrid } from "../../helpers/hangingProtocol/editorOps";
import { MPR_MIST } from "../../helpers/hangingProtocol/protocolEnums";
import "./layoutDesigner.css";

/**
 * Visual editor for a protocol's `panelsLayout`.
 *
 * MedDream has no layout preview at all — you type "Layout rows: 1, Layout
 * columns: 2" and then scroll through form sections labelled `1x1`, `1x2`,
 * mentally mapping grid coordinates to fields. This renders the actual grid and
 * clicking a view selects it for the inspector.
 *
 * Neither viewer hangs more than one panel, so the panel grid is not editable:
 * a protocol carries a single 1x1 panel and the only thing to size is the view
 * grid inside it. Imported MedDream documents may still hold several panels, so
 * those are rendered — and labelled — rather than hidden; they just cannot be
 * added to.
 *
 * Views cap at 4x4, the largest layout the basic and advance viewers both
 * render.
 */

const MAX_DIMENSION = 4;

/** Every cell of the picker, row-major. */
const PICKER_CELLS = Array.from({ length: MAX_DIMENSION * MAX_DIMENSION }, (_, i) => ({
  row: Math.floor(i / MAX_DIMENSION) + 1,
  column: (i % MAX_DIMENSION) + 1,
}));

const countRules = (group) => (group ? getOperands(group).length : 0);

/** What is worth surfacing on the cell itself, so the grid stays informative. */
function viewportSummary(viewport) {
  const parts = [];
  const series = countRules(viewport.seriesConditions);
  // The default "first image" condition is on every new viewport, so it says
  // nothing about what the author configured — see isDefaultInstanceConditions.
  const instance = isDefaultInstanceConditions(viewport.instanceConditions)
    ? 0
    : countRules(viewport.instanceConditions);
  if (series) parts.push(`${series} ${t("hanging-protocol.series-short")}`);
  if (instance) parts.push(`${instance} ${t("hanging-protocol.instance-short")}`);
  if (viewport.rotation) parts.push(`${viewport.rotation}°`);
  if (viewport.flipHorizontally) parts.push("⇋");
  if (viewport.flipVertically) parts.push("⇅");
  if (viewport.windowing) parts.push("W/L");
  return parts;
}

/** Features the basic viewer cannot render; stored and exported regardless. */
function advanceOnlyBadges(viewport) {
  const badges = [];
  if (viewport.mprMist && viewport.mprMist !== MPR_MIST.DISABLED) {
    badges.push(viewport.mprMist.toUpperCase());
  }
  if (viewport.mprSeriesInViewer && viewport.mprSeriesInViewer !== "no") badges.push("MPR");
  return badges;
}

/** Panel modes only the advance viewer honours. No editor yet — imported only. */
function panelBadges(panel) {
  const badges = [];
  if (panel.isOverview) badges.push("OVERVIEW");
  if (panel.quadrantZoomEnabled) badges.push("QUADRANT");
  if (panel.comparisonStudyProtocolEnabled) badges.push("COMPARISON");
  return badges;
}

/**
 * Word-style grid picker: hovering previews the layout it would produce and one
 * click commits both dimensions, instead of two spinners stepped separately.
 * The 4x4 board also states the cap without needing a note.
 */
function LayoutPicker({ rows, columns, disabled, onPick }) {
  const [preview, setPreview] = useState(null);
  const shown = preview || { rows, columns };

  return (
    <div className="hpv2-layout-picker">
      <div className="hpv2-layout-picker-head">
        <span className="hpv2-layout-picker-label">{t("hanging-protocol.views")}</span>
        <span className="hpv2-layout-picker-readout">
          {t("hanging-protocol.view-count", { rows: shown.rows, columns: shown.columns })}
        </span>
      </div>

      <div className="hpv2-layout-picker-grid" onMouseLeave={() => setPreview(null)}>
        {PICKER_CELLS.map(({ row, column }) => (
          <button
            type="button"
            key={`${row}-${column}`}
            className={`hpv2-layout-picker-cell ${
              row <= shown.rows && column <= shown.columns ? "is-on" : ""
            }`}
            disabled={disabled}
            // Sixteen cells need sixteen distinct names, and t() cannot carry
            // the coordinates, so they are appended.
            aria-label={`${t("hanging-protocol.views")} ${row}×${column}`}
            aria-pressed={row === rows && column === columns}
            onMouseEnter={() => setPreview({ rows: row, columns: column })}
            onFocus={() => setPreview({ rows: row, columns: column })}
            onBlur={() => setPreview(null)}
            onClick={() => onPick(row, column)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * `invalidViewport` asks the wizard whether a cell failed validation. Only the
 * selected cell has its fields on screen, so without a mark on the grid a bad
 * value elsewhere would be a Save that does nothing visible.
 */
export default function LayoutDesigner({
  panelsLayout,
  selected = { panelIndex: 0, viewportIndex: 0 },
  onSelect,
  onChange,
  disabled = false,
  invalidViewport = () => false,
}) {
  const layout = panelsLayout || { columns: 1, rows: 1, panels: [createPanel()] };
  const panels = layout.panels || [];

  // Only an imported document has more than one panel; authoring never creates
  // one, so the panel header is noise in the normal case.
  const multiPanel = panels.length > 1;

  const setViewportGrid = (panelIndex, rows, columns) => {
    const panel = panels[panelIndex];
    const viewports = resizeGrid(
      panel.layout?.viewports || [],
      rows,
      columns,
      ({ row, column }) => createViewport({ row, column })
    );
    onChange({
      ...layout,
      panels: panels.map((p, i) =>
        i === panelIndex ? { ...p, layout: { ...p.layout, rows, columns, viewports } } : p
      ),
    });
  };

  return (
    <div className="hpv2-layout-designer">
      <div
        className="hpv2-panel-grid"
        style={{
          gridTemplateColumns: `repeat(${layout.columns}, 1fr)`,
          gridTemplateRows: `repeat(${layout.rows}, auto)`,
        }}
      >
        {panels.map((panel, panelIndex) => {
          const panelLayout = panel.layout || { rows: 1, columns: 1, viewports: [] };
          const isSelectedPanel = selected?.panelIndex === panelIndex;
          const badges = panelBadges(panel);
          return (
            <div
              key={`${panel.row}-${panel.column}`}
              className={`hpv2-panel ${multiPanel ? "has-chrome" : ""} ${
                multiPanel && isSelectedPanel ? "is-selected-panel" : ""
              }`}
              style={{ gridRow: panel.row, gridColumn: panel.column }}
            >
              {multiPanel && (
                <span className="hpv2-panel-title">
                  {t("hanging-protocol.panel")} {panel.row}×{panel.column}
                </span>
              )}

              <LayoutPicker
                rows={panelLayout.rows}
                columns={panelLayout.columns}
                disabled={disabled}
                onPick={(rows, columns) => setViewportGrid(panelIndex, rows, columns)}
              />

              {badges.length > 0 && (
                <div className="hpv2-panel-flags">
                  {badges.map((badge) => (
                    <span key={badge} className="hpv2-badge hpv2-badge-advance">
                      {badge}
                    </span>
                  ))}
                </div>
              )}

              <div
                className="hpv2-viewport-grid"
                style={{
                  gridTemplateColumns: `repeat(${panelLayout.columns}, 1fr)`,
                  gridTemplateRows: `repeat(${panelLayout.rows}, 1fr)`,
                }}
              >
                {(panelLayout.viewports || []).map((viewport, viewportIndex) => {
                  const isSelected = isSelectedPanel && selected?.viewportIndex === viewportIndex;
                  const summary = viewportSummary(viewport);
                  const viewBadges = advanceOnlyBadges(viewport);
                  const willNotHang = isDeadViewport(viewport);
                  const unconfigured = summary.length === 0 && viewBadges.length === 0;
                  const invalid = invalidViewport(panelIndex, viewportIndex);
                  return (
                    <button
                      type="button"
                      key={`${viewport.row}-${viewport.column}`}
                      className={`hpv2-viewport ${isSelected ? "is-selected" : ""} ${
                        unconfigured ? "is-empty" : ""
                      } ${invalid ? "is-invalid" : ""}`}
                      style={{ gridRow: viewport.row, gridColumn: viewport.column }}
                      onClick={() => onSelect?.({ panelIndex, viewportIndex })}
                      aria-pressed={isSelected}
                    >
                      <span className="hpv2-viewport-coords">
                        {viewport.row}×{viewport.column}
                      </span>
                      {summary.length > 0 && (
                        <span className="hpv2-viewport-summary">{summary.join(" · ")}</span>
                      )}
                      {viewBadges.length > 0 && (
                        <span className="hpv2-viewport-badges">
                          {viewBadges.map((badge) => (
                            <span key={badge} className="hpv2-badge hpv2-badge-advance">
                              {badge}
                            </span>
                          ))}
                        </span>
                      )}
                      {willNotHang && (
                        <span
                          className="hpv2-viewport-warning"
                          title={t("hanging-protocol.no-instance-condition-help")}
                        >
                          {t("hanging-protocol.no-instance-condition")}
                        </span>
                      )}
                      {unconfigured && (
                        <span className="hpv2-viewport-empty">
                          {t("hanging-protocol.not-configured")}
                        </span>
                      )}
                      {/* Which field is wrong is on the field itself, once this
                          cell is the selected one — see ViewportInspector. */}
                      {invalid && (
                        <span className="hpv2-viewport-invalid">
                          {t("hanging-protocol.viewport-has-error")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <p className="hpv2-layout-hint">{t("hanging-protocol.layout-hint")}</p>
    </div>
  );
}
